import React, { useEffect } from 'react'
import { Pressable, View } from 'react-native'
import { useModalize } from 'react-native-modalize'
import Svg, { Path } from 'react-native-svg'

import CloseIcon from '@common/assets/svg/CloseIcon'
import KebabMenuIcon from '@common/assets/svg/KebabMenuIcon'
import BottomSheet from '@common/components/BottomSheet'
import Text from '@common/components/Text'
import ZKXLogo from '@common/components/ZKXLogo'
import { isMobileBrowser, mobileBrowserOS } from '@common/config/env'
import { useTranslation } from '@common/config/localization'
import { i18nKey } from '@common/config/localization/constants'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'

// One-time "Install the official ZKX Wallet app (Add to Home Screen)" bottom
// sheet, shown only on the mobile-web build running inside a real phone browser
// (iOS Safari / Android Chrome). It surfaces once — the first time the user
// opens a Send / Shield / Private-wallet screen — then never again (a single
// localStorage flag shared across all of those screens). The step instructions
// are OS-specific because the install gesture differs (Safari Share menu vs.
// Chrome three-dot menu).

const STORAGE_KEY = 'zkx:install-app-prompt:seen:v1'

type IconProps = { color: string }

// iOS Safari "Share" glyph — up arrow rising out of an open tray.
const ShareIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 3v12"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="m8 6.5 4-4 4 4"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M7 10.5H6a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 6 20.5h12a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 18 10.5h-1"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
)

// Plus glyph for "Add to Home Screen".
const PlusIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 5v14M5 12h14"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
)

// Download glyph — down arrow into a tray, for the final "Tap Add to confirm".
const DownloadIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 3v11"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="m8 10.5 4 4 4-4"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M5 19.5h14"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
)

// title/description are i18nKey('…') literals, translated at render.
type Step = {
  title: string
  description: string
  Icon: React.FC<{ color: string }>
}

const IOS_STEPS: Step[] = [
  {
    title: i18nKey('1. Tap the Share button'),
    description: i18nKey("Located in Safari's bottom toolbar"),
    Icon: ShareIcon
  },
  {
    title: i18nKey('2. Tap “Add to Home Screen”'),
    description: i18nKey('Scroll down in the share menu to find this option'),
    Icon: PlusIcon
  },
  {
    title: i18nKey('3. Tap “Add” to confirm'),
    description: i18nKey('ZKX Wallet will appear on your Home Screen'),
    Icon: DownloadIcon
  }
]

const ANDROID_STEPS: Step[] = [
  {
    title: i18nKey('1. Open browser menu'),
    description: i18nKey('Tap the three-dot menu in the top-right corner'),
    Icon: ({ color }) => <KebabMenuIcon width={4} height={20} color={color} />
  },
  {
    title: i18nKey('2. Add to Home Screen'),
    description: i18nKey('Select “Add to Home screen” to install the app'),
    Icon: PlusIcon
  }
]

const InstallAppPrompt: React.FC = () => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { ref: sheetRef, open: openBottomSheet, close: closeBottomSheet } = useModalize()

  const shouldRender = isMobileBrowser && !!mobileBrowserOS

  useEffect(() => {
    if (!shouldRender) return undefined

    let alreadySeen = false
    try {
      alreadySeen = !!window.localStorage.getItem(STORAGE_KEY)
    } catch {
      // localStorage may be unavailable (private mode / blocked) — skip silently.
      return undefined
    }
    if (alreadySeen) return undefined

    // Let the underlying screen settle before sliding the sheet up. The
    // show-once flag is persisted only when we actually open it (not up front),
    // so a screen that unmounts within this window — e.g. the public → private
    // Send redirect — doesn't burn the flag without ever showing the prompt.
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, '1')
      } catch {
        // ignore write failures
      }
      openBottomSheet()
    }, 450)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender])

  if (!shouldRender) return null

  const steps = mobileBrowserOS === 'ios' ? IOS_STEPS : ANDROID_STEPS

  return (
    <BottomSheet
      id="install-app-prompt"
      type="bottom-sheet"
      sheetRef={sheetRef}
      closeBottomSheet={closeBottomSheet}
      backgroundColor="primaryBackground"
      style={{ backgroundColor: theme.onboardingCardBackground }}
      containerInnerWrapperStyles={{ ...spacings.phLg, ...spacings.pbLg }}
    >
      {/* Close button */}
      <View style={[flexbox.directionRow, flexbox.justifyEnd, spacings.ptTy]}>
        <Pressable
          onPress={() => closeBottomSheet()}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          style={[flexbox.center, { width: 32, height: 32 }]}
        >
          <CloseIcon width={20} height={20} color={theme.secondaryText} />
        </Pressable>
      </View>

      {/* Logo + heading */}
      <View style={[flexbox.alignCenter, spacings.ph]}>
        <ZKXLogo width={64} />
        <Text
          fontSize={22}
          weight="semiBold"
          color={theme.primaryText}
          style={[{ textAlign: 'center' }, spacings.mtMd]}
        >
          {t('Use the official ZKX Wallet app')}
        </Text>
        <Text
          fontSize={15}
          weight="regular"
          color={theme.secondaryText}
          style={[{ textAlign: 'center', lineHeight: 22 }, spacings.mtTy]}
        >
          {t('Install ZKX Wallet to avoid fake or impersonated websites')}
        </Text>
      </View>

      {/* Divider */}
      <View
        style={[
          spacings.mvLg,
          { height: 1, backgroundColor: theme.secondaryBorder, width: '100%' }
        ]}
      />

      {/* Steps */}
      <View style={{ gap: 24 }}>
        {steps.map((step) => (
          <View key={step.title} style={[flexbox.directionRow, flexbox.alignCenter]}>
            <View
              style={[
                flexbox.center,
                {
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  backgroundColor: theme.onboardingIconBackground
                }
              ]}
            >
              <step.Icon color={theme.secondaryText as string} />
            </View>
            <View style={[flexbox.flex1, spacings.mlMd]}>
              <Text fontSize={16} weight="semiBold" color={theme.primaryText}>
                {t(step.title)}
              </Text>
              <Text
                fontSize={14}
                weight="regular"
                color={theme.secondaryText}
                style={[spacings.mtMi, { lineHeight: 20 }]}
              >
                {t(step.description)}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </BottomSheet>
  )
}

export default React.memo(InstallAppPrompt)
